import { useMemo, useRef } from 'react';

import { useIsomorphicLayoutEffect } from '@kc-console/core';

import { usePanelScopeRegistry } from '../stores/usePanelScopeRegistry';

import type { PanelScopeConfig } from '../stores/usePanelScopeRegistry';

/**
 * Panel Scope를 Registry에 한 번만 등록합니다.
 *
 * - 단일 scope 또는 scope 배열을 받을 수 있습니다.
 * - 서비스별 진입점/모듈에서 호출하여 분산 등록 패턴을 지원합니다.
 * - 동일 컴포넌트 재렌더 시 중복 등록을 방지합니다.
 * - 배열 참조 최적화: 단일 scope 사용 시 불필요한 배열 생성을 방지합니다.
 * - useIsomorphicLayoutEffect 사용: SSR 안전 + 자식 컴포넌트의 useEffect보다 먼저 실행되어 타이밍 이슈 방지
 *
 * @param scopes - 등록할 Panel Scope (단일 또는 배열)
 *
 * @example
 * // 단일 scope 등록 (권장)
 * useRegisterPanelScopes(ZONE_LIST_SCOPE);
 *
 * @example
 * // 여러 scope 등록
 * useRegisterPanelScopes([ZONE_LIST_SCOPE, ZONE_DETAIL_SCOPE]);
 */
export const useRegisterPanelScopes = (scopes: PanelScopeConfig | PanelScopeConfig[]) => {
  const registerScopes = usePanelScopeRegistry(state => state.registerScopes);
  const hasRegisteredRef = useRef(false);

  // 단일 scope를 배열로 변환 (참조 안정성 보장)
  const scopeArray = useMemo(() => (Array.isArray(scopes) ? scopes : [scopes]), [scopes]);

  useIsomorphicLayoutEffect(() => {
    if (hasRegisteredRef.current || scopeArray.length === 0) {
      return;
    }
    const { registry } = usePanelScopeRegistry.getState();
    const pendingScopes = scopeArray.filter(scope => !registry[scope.scopeKey]);

    if (pendingScopes.length === 0) {
      hasRegisteredRef.current = true;
      return;
    }

    registerScopes(pendingScopes);
    hasRegisteredRef.current = true;
  }, [registerScopes, scopeArray]);
};
